import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface PwaErrorBoundaryProps {
  children: ReactNode;
}

interface PwaErrorBoundaryState {
  hasError: boolean;
}

/**
 * PwaManager 専用のエラー境界。**エラー時は何も描画しない（null）。**
 *
 * PwaManager は「オフライン起動」「ストレージ枯渇」「Service Worker 非対応 WebView」
 * という最も不安定な条件下で動くことを目的にしている。それをアプリ共通の
 * ErrorBoundary の外に置くと、ここが throw した瞬間にアプリ全体が白画面になり、
 * 「接続エラーを親切に案内する」機能が「アプリを起動不能にする」機能に反転する。
 *
 * 共通の ErrorBoundary を使わないのは、あちらがエラー画面を描画してしまうため。
 * PWA の付加機能が落ちただけでアプリ本体の画面を潰す理由は無いので、
 * ここでは静かに機能を落として本体の描画を続ける。
 */
class PwaErrorBoundary extends Component<PwaErrorBoundaryProps, PwaErrorBoundaryState> {
  constructor(props: PwaErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): PwaErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 画面には出さないが、原因追跡の手掛かりは残す（PWA 機能だけが無効になる）。
    console.warn('[PwaManager] PWA 機能でエラーが発生したため無効化しました。', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default PwaErrorBoundary;
